import React, { useState } from 'react';
import {
  Container,
  Box,
  Typography,
  Paper,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import Editor from '@monaco-editor/react';
import SingleFileUpload from '../components/SingleFileUpload';
import { submitJob } from '../api';

const singleAnalysisScriptTemplate = `-- ========== HELPER FUNCTION ==========
-- Add a new column to the sheet
function addColumn(sheet, columnName, values)
  for i, row in ipairs(sheet) do
    if i == 1 then
      table.insert(row, columnName)
    else
      table.insert(row, values[i - 1])
    end
  end
end
-- Check if all cells in a given column range (1-based) are empty/false
function isEmptyRange(row, startCol, endCol)
  for i = startCol, endCol do
    local val = row[i]
    if val and val ~= "" and val ~= "FALSE" and val ~= false then
      return false
    end
  end
  return true
end
-- ========== USER LOGIC ==========
function userLogic(sheet)
  local correctionColumn = {}
  -- Find the "Overdue Category" column index from header row
  local header = sheet[1]
  local overdueIndex = -1
  for i, colName in ipairs(header) do
    if colName == "Overdue Category" then
      overdueIndex = i
      break
    end
  end
  if overdueIndex == -1 then
    error("Overdue Category column not found")
  end
  -- Map Excel column letters to Lua (1-based) indices
  local colMap = {
    C = 3, K = 11, O = 15, W = 23, AB = 28
  }
  for i = 2, #sheet do
    local row = sheet[i]
    local category = row[overdueIndex]
    local result = "Please update due date"
    if category == "Not Due" then
      if isEmptyRange(row, colMap["C"], colMap["AB"]) then
        result = "Nil"
      end
    elseif category == "0 - 14" then
      if isEmptyRange(row, colMap["K"], colMap["AB"]) then
        result = "Nil"
      end
    elseif category == "15 - 30" then
      if isEmptyRange(row, colMap["O"], colMap["AB"]) then
        result = "Nil"
      end
    elseif category == "31 - 60" then
      if isEmptyRange(row, colMap["W"], colMap["AB"]) then
        result = "Nil"
      end
    end
    table.insert(correctionColumn, result)
  end
  addColumn(sheet, "Correction Needed", correctionColumn)
end
userLogic(sheet)
`;

const SingleSpreadsheetAnalysis = () => {
  const [sheet, setSheet] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<string[][]>([]);
  const [script, setScript] = useState<string>(singleAnalysisScriptTemplate);
  const [error, setError] = useState<string>('');

  const getFileBaseName = (filename: string) =>
    filename.replace(/\.[^/.]+$/, '');

  const toTitleCase = (str: string) =>
    str.replace(/_/g, ' ').replace(/\w\S*/g, txt =>
      txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
    );

//   const handleUpload = async (file: File | null) => {
//     setSheet(file);
//     setError('');
//     setPreviewData([]);

//     if (!file) return;

//     const formData = new FormData();
//     formData.append('sheet', file);

//     try {
//         const res = await fetch('/api/preview-single', {
//             method: 'POST',
//             body: formData,
//           });

//         // Log the response for debugging
//         const text = await res.text();
//         console.log('Raw Response Text:', text);

//         // Try parsing the response manually
//         try {
//           const data = JSON.parse(text); // Manually parse JSON
//           const sheetColumns = data.columns || [];
//           const sheetRows = data.data || [];

//           const fullSheet = [sheetColumns, ...sheetRows];
//           setPreviewData(fullSheet.slice(0, 6)); // Preview first 5 rows + header
//         } catch (err) {
//           console.error('Error parsing JSON:', err);
//           setError('There was an error loading the spreadsheet preview.');
//         }
//     } catch (err) {
//       console.error('Preview error:', err);
//       setError('There was an error loading the spreadsheet preview.');
//     }
//   };


const handleUpload = async (file: File | null) => {
    setSheet(file);
    setError('');
    setPreviewData([]);

    if (!file) return;

    const formData = new FormData();
    formData.append('sheet', file);

    try {
        const res = await fetch('/api/preview-single', {
            method: 'POST',
            body: formData,
        });

        // Log the raw response text for debugging
        const text = await res.text();
        console.log('Raw Response Text:', text);

        // Try parsing the response manually
        try {
            const data = JSON.parse(text); // Manually parse JSON
            const sheetColumns = data.columns || [];
            const sheetRows = data.data || [];

            const fullSheet = [sheetColumns, ...sheetRows];
            setPreviewData(fullSheet.slice(0, 6)); // Preview first 5 rows + header
        } catch (err) {
            console.error('Error parsing JSON:', err);
            setError('There was an error loading the spreadsheet preview.');
        }
    } catch (err) {
        console.error('Preview error:', err);
        setError('There was an error loading the spreadsheet preview.');
    }
};


  const handleRun = async () => {
    if (!script || !sheet) return;

    try {
      await submitJob(
        '/api/analyze',
        { sheet },
        { script },
        'analysis_result.xlsx',
      );
      console.log('Analysis file downloaded successfully.');
    } catch (err) {
      console.error('Error during Lua execution:', err);
      setError('There was an error while running the analysis.');
    }
  };

  const renderTable = (data: string[][]) => (
    <Box sx={{ marginTop: '30px' }}>
      <Typography variant="h6" sx={{ fontWeight: 'medium', marginBottom: '10px' }}>
        {sheet ? `${toTitleCase(getFileBaseName(sheet.name))} PREVIEW` : 'Preview'}
      </Typography>
      <TableContainer component={Paper} sx={{ maxHeight: 300 }}>
        <Table stickyHeader size="small">
          <TableHead>
            <TableRow>
              {data[0]?.map((cell, idx) => (
                <TableCell key={idx} sx={{ fontWeight: 'bold' }}>
                  {cell}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.slice(1).map((row, i) => (
              <TableRow key={i}>
                {row.map((cell, j) => (
                  <TableCell key={j}>{cell}</TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );

  return (
    <Container maxWidth="lg" sx={{ textAlign: 'center', paddingTop: '50px' }}>
      <Box sx={{ backgroundColor: '#183B4E', color: 'white', padding: '20px', borderRadius: '8px' }}>
        <Typography variant="h3" sx={{ fontWeight: 'bold', marginBottom: '20px' }}>
          Single Spreadsheet Analysis
        </Typography>
        <Typography variant="h6" sx={{ fontStyle: 'italic', marginBottom: '40px' }}>
          Upload one spreadsheet and write Lua to analyze it.
        </Typography>
      </Box>

      <Paper elevation={6} sx={{ padding: '30px', borderRadius: '8px', marginTop: '30px', backgroundColor: '#f5f5f5' }}>
        <SingleFileUpload onUpload={handleUpload} />

        {error && <Typography color="error">{error}</Typography>}

        {previewData.length > 0 && renderTable(previewData)}

        {sheet && (
          <>
            <Typography
              variant="h6"
              sx={{ fontWeight: 'medium', marginTop: '10px', marginBottom: '10px' }}
            >
              CUSTOM SCRIPT
            </Typography>

            <Editor
              height="300px"
              language="lua"
              value={script}
              onChange={(value) => setScript(value ?? '')}
              theme="vs-dark"
              options={{
                fontSize: 14,
                minimap: { enabled: false },
                scrollBeyondLastLine: false,
                fontLigatures: true,
                fontFamily: '"Fira Code", monospace',
                wordWrap: 'on',
                tabSize: 2
              }}
            />

            <Button
              variant="contained"
              sx={{ marginTop: '30px', backgroundColor: '#27548A' }}
              onClick={handleRun}
              disabled={!sheet || !script}
            >
              Run Analysis
            </Button>
          </>
        )}
      </Paper>
    </Container>
  );
};

export default SingleSpreadsheetAnalysis;
